import { ListItem, Tooltip, lighten, styled, useTheme } from '@mui/material';
import { ReactElement } from 'react';
import {
  Link as RouterLink,
  NavLink as RouterNavLink,
  useLocation,
} from 'react-router-dom';
import Button from 'switch-fe-shared/components/Buttons/Button';

interface CollapsibleMenuItemProps {
  to: string;
  icon: ReactElement;
  label: string;
  isCollapsed: boolean;
  onClick?: () => void;
  disabled?: boolean;
  activeWhen?: (pathname: string) => boolean;
}

const StyledListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed: boolean }>(
  ({ theme, collapsed }) => `
    padding: 1px 0;

    .MuiButton-root {
      display: flex;
      color: ${theme.palette.text.primary};
      background-color: transparent;
      width: 100%;
      justify-content: ${collapsed ? 'center' : 'flex-start'};
      padding: ${collapsed ? theme.spacing(1.2) : theme.spacing(1.2, 3)};
      min-width: ${collapsed ? '50px' : 'auto'};
      border-radius: ${collapsed ? '8px' : theme.shape.borderRadius}px;
      margin: ${collapsed ? '0 8px' : '0'};

      .MuiButton-startIcon {
        transition: ${theme.transitions.create(['color'])};
        color: ${theme.sidebar.menuItemIconColor};
        font-size: ${theme.typography.pxToRem(20)};
        margin-right: ${collapsed ? '0' : theme.spacing(1)};
        margin-left: 0;

        .MuiSvgIcon-root {
          font-size: inherit;
          transition: none;
        }
      }

      .MuiButton-label {
        opacity: ${collapsed ? '0' : '1'};
        transition: opacity 0.2s ease-in-out;
        padding-top: 3px;
        width: ${collapsed ? '0' : 'auto'};
        overflow: hidden;
        white-space: nowrap;
      }

      &.active,
      &:hover {
        background-color: ${lighten(theme.colors.primary.main, 0.9)};
        color: ${theme.sidebar.menuItemColorActive};

        .MuiButton-startIcon {
          color: ${theme.sidebar.menuItemIconColorActive};
        }
      }
    }
`
);

const CollapsibleMenuItem = ({
  to,
  icon,
  label,
  isCollapsed,
  onClick,
  disabled = false,
  activeWhen,
}: CollapsibleMenuItemProps) => {
  /* Hooks & Use Effects */
  const theme = useTheme();
  const location = useLocation();
  const isActive = activeWhen?.(location.pathname);

  /* States */

  /* API Calls */

  /* Functions & Functional Components */
  const activeProps =
    activeWhen === undefined
      ? {}
      : {
          className: isActive ? 'active' : undefined,
        };

  const menuItem = (
    <StyledListItem collapsed={isCollapsed}>
      <Button
        disableRipple
        component={activeWhen === undefined ? RouterNavLink : RouterLink}
        onClick={onClick}
        to={to}
        startIcon={icon}
        variant='text'
        disabled={disabled}
        {...activeProps}
        sx={{
          '& .MuiButton-startIcon': {
            marginRight: isCollapsed ? 0 : 1,
          },
        }}
      >
        {!isCollapsed && <span className='MuiButton-label'>{label}</span>}
      </Button>
    </StyledListItem>
  );

  if (isCollapsed) {
    return (
      <Tooltip title={label} placement='right' enterDelay={500}>
        {menuItem}
      </Tooltip>
    );
  }

  return menuItem;
};

export default CollapsibleMenuItem;
